/**
 * `<ProDocColumnMapper>` — a grid's columns mapped once, by a person (engine W32,
 * FR-E48, FR-E49, FR-E55; plan D119 – D122, D130).
 *
 * A file of a **mapped** profile (`books_ledger` first) that no saved map fits is
 * refused `mapping_needed` — never guessed (FR-E49). The refusal's job carries
 * what a person needs to map it: the sheets, up to five rows that may be the
 * header (their cells as printed) and a **suggestion** from the profile's own
 * headings vocabulary. This screen opens with that suggestion filled in, and the
 * person:
 *
 * - picks the **sheet** and the **header row** among the candidates;
 * - gives each column a **field** from the profile's `map_fields` — the required
 *   ones marked, and the money rule held: a date, and a debit or a credit (or,
 *   signed, one amount);
 * - sets the **rules**: how dates are printed, how money is signed, which rows to
 *   skip (blank, a repeated header, a total line by its text) and where the table
 *   stops;
 * - asks for a **preview** — the engine's own reading of the file under the
 *   unsaved map (D122): the first rows, the summary and the checks, unmetered;
 * - and **saves** it — a new map, or a new version of `existing` — with the
 *   preview's header fingerprint, so the map is chosen for files of this layout.
 *   The host is called back with the map, and asks for the reading again.
 *
 * **Per profile** (W38, D179): what a map must say beyond its required fields is the
 * profile's `MapperRules` — `books_ledger`'s money (a debit or a credit, or one signed
 * amount) and its date or party, as before; a register's two bindings, where a money field
 * reads **one column per rate** (`rate:<head>` — `5% Taxable`, `18% Taxable`, the rate
 * taken from each heading by the engine, never here) or **adds several columns** (a list —
 * a taxable value spread over expense-ledger columns). A host may pass its own `rules`.
 *
 * A column is named by its header's text where the header row prints one (and
 * only one column prints it), else by its letter (`header:Debit`, `D`). **Money is
 * integer paise** in every figure shown, formatted by the package's one author of
 * money — never a float. Like every ADR-0010 component it takes the host's client,
 * or the host's three props, and imports nothing of the host's.
 */
import { Alert, Badge, Button, Checkbox, DataTable, DescriptionList, Detail, Input, RadioGroup, Select, cn, formatNumber, type ColumnDef } from '@fiveox/ui'
import { Eye, Plus, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ChecksCard, ReconciledBadge } from './extraction'
import {
  MAPPING_NEEDED_COPY,
  MAP_DATE_FORMATS,
  MAX_MAP_NAME,
  MAX_SUMMED_COLUMNS,
  RATE_PREFIX,
  columnIndex,
  columnLetter,
  fieldLabel,
  fieldLabel as packageLabel,
  fieldText,
  jobEnded,
  jobReasonText,
  type ColumnMap,
  type HeaderCandidate,
  type MapBinding,
  type MapDateFormat,
  type MapField,
  type MapPreview,
  type MapSkip,
  type MapSpec,
  type MappingNeeded,
  type MoneyMode,
  type ParseProfile,
} from './extraction-model'
import { errorText, type HostProps } from './hooks'
import { JOB_POLL_MS, followJob } from './jobs'
import { ApiError, createProDocClient, documentTypeLabel, type DocumentTypeOption, type ProDocClient } from './prodoc-client'

/* ------------------------------------------------------------ the model */

/** The fields that sign money one way or the other (the contract's rule for `books_ledger`). */
const DEBIT_CREDIT = ['debit', 'credit']
const SIGNED = ['amount']
/**
 * Engine W33: a ledger's map names its rows by a date **or** a party — neither is required
 * by itself (a party-level ledger prints no date), but one of the two is. The service
 * refuses a map with neither, `required:date_or_party`.
 */
const DATE_OR_PARTY = ['date', 'party']
/** What a person is told when a map binds neither — never the service's code. */
export const DATE_OR_PARTY_TEXT = 'Choose the column holding the date or the party — at least one is needed.'
const DATE_OR_PARTY_ITEM = 'Date or party'

/** Two or more fields of which a map must bind one — applied where the profile has each and requires none of them. */
export interface OneOfRule {
  fields: readonly string[]
  /** The item said in "Still to map". */
  item: string
  /** The sentence a person is told when the map binds none of them. */
  text: string
}

/**
 * What a profile's map must say beyond its required fields (W38, D179) — the rules that were
 * `books_ledger`'s alone until W38, as configuration:
 *
 * - `money` — the fields that sign money one way (a `debit` and a `credit` column) or the
 *   other (one signed `amount`), offered as two modes where the profile has both;
 * - `oneOf` — fields of which one must be bound (a ledger's date or party);
 * - `rateHeads` — the heads a money field may read **one column per rate** (`rate:<head>`);
 * - `summed` — whether a money field may **add several columns**;
 * - `ledgerOf` — W38 follow-up (D-48): the text fields that may be **named by the ledger column a
 *   line is posted to** (`{ledger_of: [columns]}` — a sales register's item).
 */
export interface MapperRules {
  money: { debitCredit: readonly string[]; signed: readonly string[] } | null
  oneOf: readonly OneOfRule[]
  rateHeads: readonly string[]
  summed: boolean
  ledgerOf?: readonly string[]
}

/** `books_ledger`'s rules — and every mapped profile's before W38 (`form26as`'s map): each applies only where its fields exist. */
export const LEDGER_RULES: MapperRules = {
  money: { debitCredit: DEBIT_CREDIT, signed: SIGNED },
  oneOf: [{ fields: DATE_OR_PARTY, item: DATE_OR_PARTY_ITEM, text: DATE_OR_PARTY_TEXT }],
  rateHeads: [],
  summed: false,
}

/** The heads a rate-split register prints a column per rate for (ProGST's `_RATE_HEADS`; UTGST is SGST's). */
export const RATE_HEADS = ['taxable', 'igst', 'cgst', 'sgst'] as const

/** What a person is told when a register's map binds neither its taxable value nor its invoice value. */
export const TAXABLE_OR_GROSS_TEXT = 'Choose the column holding the taxable value — or, where the register prints only the gross, the invoice value.'

/**
 * A register's rules (W38, D176): its money fields may read per rate or add several columns; its
 * item may be named by the ledger a line is posted to (W38 follow-up, D-48); and a map binds the
 * taxable value or — a gross-only note register — the invoice value.
 */
export const REGISTER_RULES: MapperRules = {
  money: null,
  oneOf: [{ fields: ['taxable', 'invoice_value'], item: 'Taxable value or invoice value', text: TAXABLE_OR_GROSS_TEXT }],
  rateHeads: RATE_HEADS,
  summed: true,
  ledgerOf: ['item'],
}

/** The rules by profile. A profile not named here keeps `LEDGER_RULES`, as every map did before W38. */
export const MAPPER_RULES: ReadonlyMap<string, MapperRules> = new Map([
  ['books_ledger', LEDGER_RULES],
  ['sales_register', REGISTER_RULES],
  ['purchase_register', REGISTER_RULES],
])

/** A profile's rules: the table's, else `LEDGER_RULES`. */
export const mapperRules = (profile: string): MapperRules => MAPPER_RULES.get(profile) ?? LEDGER_RULES

/** How long a preview that did not end inside its `wait` is followed before the screen says so. */
const PREVIEW_FOLLOW_MS = 90_000

const norm = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase()

const applies = (rule: OneOfRule, fields: readonly MapField[]) => {
  const found = rule.fields.map((n) => fields.find((f) => f.name === n))
  return found.every((f) => f !== undefined && !f.required)
}

/** Whether a map of this profile must bind the date or the party, one of the two (W33). */
export function dateOrParty(fields: readonly MapField[]): boolean {
  return applies(LEDGER_RULES.oneOf[0]!, fields)
}

/** Whether the profile can sign money both ways — a debit / credit column each, or one signed amount. */
export function hasMoneyModes(fields: readonly MapField[], rules: MapperRules = LEDGER_RULES): boolean {
  if (!rules.money) return false
  const names = new Set(fields.map((f) => f.name))
  return rules.money.signed.some((n) => names.has(n)) && rules.money.debitCredit.some((n) => names.has(n))
}

/**
 * The money mode the map is sent with: the person's where the profile signs money both ways;
 * `signed` where it has the signed amount and no debit or credit (the service's rule would
 * otherwise ask for a debit or a credit the profile does not have); else the person's.
 */
export function moneyModeFor(fields: readonly MapField[], mode: MoneyMode, rules: MapperRules = LEDGER_RULES): MoneyMode {
  if (!rules.money || hasMoneyModes(fields, rules)) return mode
  const names = new Set(fields.map((f) => f.name))
  const signed = rules.money.signed.some((n) => names.has(n))
  const debitCredit = rules.money.debitCredit.some((n) => names.has(n))
  return signed && !debitCredit ? 'signed' : mode
}

/** The fields a column may be given under a money mode: `amount` is signed mode's, `debit` and `credit` the other's. */
export function offeredFields(fields: readonly MapField[], mode: MoneyMode, rules: MapperRules = LEDGER_RULES): MapField[] {
  if (!rules.money || !hasMoneyModes(fields, rules)) return [...fields]
  const hidden = mode === 'signed' ? rules.money.debitCredit : rules.money.signed
  return fields.filter((f) => !hidden.includes(f.name))
}

/**
 * What a map still needs before the service would take it: every required field
 * bound, the money rule — a debit or a credit column (`debit_credit`), or the
 * amount (`signed`) — and, for a ledger (W33), the date or the party. Each item is
 * said as what is missing. A field read per rate or added from several columns counts
 * as bound (W38).
 */
export function missingBindings(
  fields: readonly MapField[],
  bound: ReadonlySet<string>,
  mode: MoneyMode,
  rules: MapperRules = LEDGER_RULES,
  labelOf: (field: string) => string = fieldLabel,
): string[] {
  const names = new Set(fields.map((f) => f.name))
  const missing = offeredFields(fields, mode, rules)
    .filter((f) => f.required && !bound.has(f.name))
    .map((f) => labelOf(f.name))
  if (rules.money) {
    const { signed, debitCredit } = rules.money
    if (mode === 'signed' && signed.some((n) => names.has(n)) && !signed.some((n) => bound.has(n))) missing.push('Amount')
    if (mode === 'debit_credit' && debitCredit.some((n) => names.has(n)) && !debitCredit.some((n) => bound.has(n))) missing.push('Debit or credit')
  }
  for (const rule of rules.oneOf) if (applies(rule, fields) && !rule.fields.some((n) => bound.has(n))) missing.push(rule.item)
  return [...new Set(missing)]
}

/**
 * A map problem the service names (`details.problems`, `required:<field>` and the rules'
 * codes) said as a sentence — never the code itself. `null` for one this screen does not
 * word, which then falls back to the refusal's own message.
 */
export function problemText(code: string): string | null {
  if (code === 'required:date_or_party') return DATE_OR_PARTY_TEXT
  if (code === 'required:debit_or_credit') return 'Choose the column holding the debit or the credit — at least one is needed.'
  if (code === 'required:amount') return 'Choose the column holding the amount.'
  if (code === 'required:taxable_or_invoice_value') return TAXABLE_OR_GROSS_TEXT
  if (code.startsWith('required:')) return `Choose the column holding the ${fieldLabel(code.slice('required:'.length)).toLowerCase()}.`
  return null
}

/** A refused preview or save, said plainly: its map problems as sentences, else its message. */
export function mapErrorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError && Array.isArray(e.details.problems)) {
    const texts = (e.details.problems as unknown[]).flatMap((p) => {
      const text = typeof p === 'string' ? problemText(p) : null
      return text ? [text] : []
    })
    if (texts.length > 0) return [...new Set(texts)].join(' ')
  }
  return errorText(e, fallback)
}

/** A column reference resolved against a header row: its letter, or the one cell printing its text. */
export function resolveColumn(ref: string, cells: readonly string[]): number | null {
  if (ref.startsWith('header:')) {
    const want = norm(ref.slice('header:'.length))
    if (!want) return null
    const at = cells.map(norm).flatMap((c, i) => (c === want ? [i] : []))
    return at.length === 1 ? at[0]! : null
  }
  return columnIndex(ref)
}

/**
 * How a column is named in the map: by its header's text as printed when the header
 * row prints text there that no other column repeats, by its letter otherwise.
 */
export function columnRef(index: number, cells: readonly string[]): string {
  const text = cells[index] ?? ''
  if (text.trim() && cells.filter((c) => norm(c) === norm(text)).length === 1) return `header:${text}`
  return columnLetter(index)
}

/* ---------------------------------------------- W38: per rate, and added */

/**
 * A field read from more than one column (W38, D176): one column per rate, or several added —
 * or (W38 follow-up, D-48) named by the one ledger column among several a line is posted to.
 */
export type MultiBinding = { kind: 'rate'; head: string } | { kind: 'sum'; columns: number[] } | { kind: 'ledger'; columns: number[] }

/** The fields that may read per rate or add columns under `rules` — a profile's money fields, where its rules allow either — and the text fields its rules let a ledger column name. */
export function multiFields(fields: readonly MapField[], rules: MapperRules): MapField[] {
  const ledgers = new Set(rules.ledgerOf ?? [])
  const money = rules.rateHeads.length > 0 || rules.summed
  return fields.filter((f) => (money && f.kind === 'money') || (ledgers.has(f.name) && f.kind !== 'money'))
}

/** The head a field reads per rate by default: the head its name holds (`taxable_value` → `taxable`), else the first. */
export function defaultHead(field: string, heads: readonly string[]): string {
  return heads.find((h) => field.toLowerCase().includes(h)) ?? heads[0] ?? 'taxable'
}

/** `5% Taxable`, `18.00 % IGST Tax` — a rate first, then its head's words. */
const RATE_COLUMN = /^\s*(\d+(?:\.\d+)?)\s*%\s*(.+)$/
/** Busy's: the head first, the rate last — `CGST Txbl. Amt @ 2.5%`, `SGST Tax Amt @ 2.5%`. */
const BUSY_RATE_COLUMN = /^\s*(cgst|sgst|utgst|igst)\s+(txbl|taxable|tax)\b[^@]*@\s*(\d+(?:\.\d+)?)\s*%\s*$/i
const HEAD_WORDS: Record<string, RegExp> = {
  taxable: /\b(taxable|txbl)\b/,
  igst: /\bigst\b/,
  cgst: /\bcgst\b/,
  sgst: /\b(sgst|utgst)\b/,
}

/**
 * The header's columns that look like `head`'s per-rate columns — **for a person to see
 * what the binding reads, never to decide it**: which rate a column carries (a CGST
 * column's is half its supply's) is the engine's rule, read from the header there.
 */
export function rateColumns(cells: readonly string[], head: string): number[] {
  const words = HEAD_WORDS[head] ?? new RegExp(`\\b${head.replace(/[^a-z0-9_]/gi, '')}\\b`, 'i')
  return cells.flatMap((cell, i) => {
    const busy = BUSY_RATE_COLUMN.exec(cell)
    if (busy) {
      const named = busy[1]!.toLowerCase() === 'utgst' ? 'sgst' : busy[1]!.toLowerCase()
      return (busy[2]!.toLowerCase() === 'tax' ? named : 'taxable') === head ? [i] : []
    }
    const match = RATE_COLUMN.exec(cell)
    return match && words.test(match[2]!.toLowerCase()) ? [i] : []
  })
}

interface Draft {
  sheet: string | null
  row: number | null
  /** field → its column, 0-based. */
  bindings: Record<string, number>
  /** W38: field → its per-rate or added binding (never also in `bindings`). */
  multi: Record<string, MultiBinding>
  dateFormat: MapDateFormat | null
  mode: MoneyMode
  skipBlank: boolean
  skipRepeat: boolean
  skipTexts: string[]
  stopAt: string
}

const candidatesOn = (mapping: MappingNeeded, sheet: string | null) => mapping.candidates.filter((c) => sheet === null || c.sheet === sheet)

/** The bindings a spec's columns name, resolved against a header row — what cannot be placed there is left out. */
function bindingsOf(spec: MapSpec | null, cells: readonly string[], fields: readonly MapField[]): Pick<Draft, 'bindings' | 'multi'> {
  const known = new Set(fields.map((f) => f.name))
  const bindings: Record<string, number> = {}
  const multi: Record<string, MultiBinding> = {}
  const taken = new Set<number>()
  for (const [field, ref] of Object.entries(spec?.columns ?? {})) {
    if (!known.has(field)) continue
    if (typeof ref === 'object' && !Array.isArray(ref)) {
      const columns = ref.ledger_of.map((r) => resolveColumn(r, cells)).filter((at): at is number => at !== null && at < cells.length)
      if (columns.length > 0) multi[field] = { kind: 'ledger', columns: [...new Set(columns)] }
      continue
    }
    if (Array.isArray(ref)) {
      const columns = ref.map((r) => resolveColumn(r, cells)).filter((at): at is number => at !== null && at < cells.length)
      if (columns.length > 0) multi[field] = { kind: 'sum', columns: [...new Set(columns)] }
      continue
    }
    if (ref.startsWith(RATE_PREFIX)) {
      multi[field] = { kind: 'rate', head: ref.slice(RATE_PREFIX.length) }
      continue
    }
    const at = resolveColumn(ref, cells)
    if (at === null || at >= cells.length || taken.has(at)) continue
    bindings[field] = at
    taken.add(at)
  }
  return { bindings, multi }
}

/** The screen's first state: the saved map's newest version, else the refusal's suggestion, else the first candidate and nothing bound. */
function draftOf(mapping: MappingNeeded, fields: readonly MapField[], spec: MapSpec | null): Draft {
  const sheets = mapping.sheets.map((s) => s.name)
  const firstSheet = mapping.candidates[0]?.sheet ?? sheets[0] ?? null
  const sheet = spec && spec.sheet !== null && (sheets.includes(spec.sheet) || mapping.candidates.some((c) => c.sheet === spec.sheet)) ? spec.sheet : firstSheet
  const on = candidatesOn(mapping, sheet)
  const candidate = on.find((c) => c.row === spec?.header_row) ?? on[0] ?? null
  const skip = spec?.rules.skip ?? ['blank', 'repeat_header']
  return {
    sheet,
    row: candidate?.row ?? null,
    ...(candidate ? bindingsOf(spec, candidate.cells, fields) : { bindings: {}, multi: {} }),
    dateFormat: spec?.rules.date_format ?? null,
    mode: spec?.rules.money.mode ?? 'debit_credit',
    skipBlank: skip.includes('blank'),
    skipRepeat: skip.includes('repeat_header'),
    skipTexts: skip.filter((s) => s.startsWith('text:')).map((s) => s.slice('text:'.length)),
    stopAt: spec?.rules.stop_at ?? '',
  }
}

/** Whether a field's per-rate, added or ledger binding is whole: a head, at least two columns added, or a ledger column. */
export const multiDone = (m: MultiBinding | undefined): boolean =>
  m !== undefined && (m.kind === 'rate' ? m.head.length > 0 : m.kind === 'ledger' ? m.columns.length >= 1 : m.columns.length >= 2)

/**
 * The map the draft says, in the contract's shape — only the fields the money mode offers,
 * each column by `columnRef`; a field read per rate as `rate:<head>`, one added from several
 * columns as their list, in the sheet's order (W38, D176).
 */
export function specOf(draft: Draft, candidate: HeaderCandidate | null, fields: readonly MapField[], rules: MapperRules = LEDGER_RULES): MapSpec {
  const mode = moneyModeFor(fields, draft.mode, rules)
  const offered = new Set(offeredFields(fields, mode, rules).map((f) => f.name))
  const multiOk = new Set(multiFields(fields, rules).map((f) => f.name))
  const cells = candidate?.cells ?? []
  const columns: Record<string, MapBinding> = {}
  for (const f of fields) {
    if (!offered.has(f.name)) continue
    const multi = multiOk.has(f.name) ? draft.multi[f.name] : undefined
    if (multi?.kind === 'rate') columns[f.name] = `${RATE_PREFIX}${multi.head}`
    else if (multi?.kind === 'ledger') {
      if (multi.columns.length > 0) columns[f.name] = { ledger_of: [...multi.columns].sort((a, b) => a - b).slice(0, MAX_SUMMED_COLUMNS).map((at) => columnRef(at, cells)) }
    } else if (multi?.kind === 'sum') {
      if (multi.columns.length > 0) columns[f.name] = [...multi.columns].sort((a, b) => a - b).slice(0, MAX_SUMMED_COLUMNS).map((at) => columnRef(at, cells))
    } else {
      const at = draft.bindings[f.name]
      if (at !== undefined) columns[f.name] = columnRef(at, cells)
    }
  }
  const skip: MapSkip[] = []
  if (draft.skipBlank) skip.push('blank')
  if (draft.skipRepeat) skip.push('repeat_header')
  for (const text of draft.skipTexts) if (text.trim()) skip.push(`text:${text.trim()}`)
  return {
    sheet: candidate ? candidate.sheet : draft.sheet,
    header_row: candidate?.row ?? draft.row ?? 1,
    columns,
    rules: { date_format: draft.dateFormat, money: { mode }, skip, stop_at: draft.stopAt.trim() || null },
  }
}

const DATE_FORMAT_TEXT: Record<MapDateFormat, string> = {
  'DD/MM/YYYY': '31/03/2026',
  'DD-MM-YYYY': '31-03-2026',
  'DD.MM.YYYY': '31.03.2026',
  'YYYY-MM-DD': '2026-03-31',
  'DD-MMM-YYYY': '31-Mar-2026',
  'MM/DD/YYYY': '03/31/2026 — month first',
}

const sheetName = (name: string) => (name === '' ? 'The file' : name)

/* ------------------------------------------------------------ the props */

export interface ColumnMapperOwnProps {
  /** The document whose file is mapped. */
  documentId: string
  /** The version the refusal read; omitted, the current one. */
  versionId?: string | null
  /** The profile, as `client.parsing.profiles()` lists it — with its `map_fields`. */
  profile: ParseProfile
  /** The refused job's `mapping`: the sheets, the header candidates and the suggestion. */
  mapping: MappingNeeded
  /** Whose map it is: `org` (the default) or `client:<id>` — the host's to choose, as its parse's `map_scope`. */
  scope?: string
  /** A saved map to change: Save then adds a version to it rather than making another. */
  existing?: ColumnMap | null
  /** The served document types, for the profile's name. */
  types?: DocumentTypeOption[]
  /** After a save, with the map as it now stands. The host then asks for the reading again. */
  onSaved: (map: ColumnMap) => void
  onCancel?: () => void
  /** How soon a preview that did not end at once is first asked about again. A test passes a few milliseconds. */
  jobPollMs?: number
  /**
   * W38 (D179): what this profile's map must say beyond its required fields — the money
   * modes, the one-of pairs, the per-rate and added bindings. Omitted, the profile's own
   * (`mapperRules(profile.name)`).
   */
  rules?: MapperRules
  /**
   * W38 follow-up: the host's own words for the profile's fields — `{taxable: 'Taxable value',
   * invoice_type: 'Invoice type (B2B, SEZ, export)'}` — where it lists its own meanings. A field
   * not named here keeps the package's label.
   */
  fieldLabels?: Readonly<Record<string, string>>
  /**
   * W40 (D186): a new map's name as the host would give it — ProGST's sheet and month. The
   * person may change it; a saved map being changed (`existing`) keeps its own name.
   */
  defaultName?: string
  /**
   * W40 (D187): each preview that reads, as the screen shows it — its rows, summary, checks and
   * headers — so the host can offer its own act on rows not yet read (ProGST's *Remember this
   * buyer as SEZ*). Not called for a refused, failed or overtaken preview. The host keeps it for
   * the screen; the package sends and keeps nothing more.
   */
  onPreview?: (preview: MapPreview) => void
  className?: string
}

/** The host's client — `<ExtractionsPanel>`'s, say — or the three props every ADR-0010 component takes. */
export type ProDocColumnMapperProps = ColumnMapperOwnProps & ({ client: ProDocClient } | (HostProps & { client?: undefined }))

export function ProDocColumnMapper(props: ProDocColumnMapperProps) {
  const given = props.client
  const host = given ? null : (props as HostProps)
  const built = useMemo(
    () => given ?? createProDocClient({ baseUrl: host!.apiUrl, fetcher: host!.fetcher, orgId: host!.orgId }),
    // The host's three, or its client — whichever it handed over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [given, host?.apiUrl, host?.fetcher, host?.orgId],
  )
  return <ColumnMapper {...props} client={built} />
}

/* ------------------------------------------------------------ the screen */

interface Shown {
  key: string
  preview: MapPreview
}

function ColumnMapper({
  client,
  documentId,
  versionId,
  profile,
  mapping,
  scope = 'org',
  existing = null,
  types = [],
  onSaved,
  onCancel,
  jobPollMs = JOB_POLL_MS,
  rules: given,
  fieldLabels,
  defaultName,
  onPreview,
  className,
}: ColumnMapperOwnProps & { client: ProDocClient }) {
  const fields = useMemo(() => profile.map_fields ?? [], [profile.map_fields])
  const rules = given ?? mapperRules(profile.name)
  const fieldLabel = (name: string): string => fieldLabels?.[name] ?? packageLabel(name)
  const [draft, setDraft] = useState<Draft>(() => draftOf(mapping, fields, existing?.version?.spec ?? mapping.suggestion))
  const [name, setName] = useState(existing?.name ?? (defaultName?.trim() || ''))
  const [shown, setShown] = useState<Shown | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [tried, setTried] = useState(false)
  const stop = useRef<AbortController | null>(null)
  useEffect(() => () => stop.current?.abort(), [])

  const sheets = mapping.sheets.length > 0 ? mapping.sheets : [...new Set(mapping.candidates.map((c) => c.sheet))].map((n) => ({ name: n, rows: 0 }))
  const onSheet = candidatesOn(mapping, draft.sheet)
  const candidate = onSheet.find((c) => c.row === draft.row) ?? null
  const mode = moneyModeFor(fields, draft.mode, rules)
  const offered = offeredFields(fields, mode, rules)
  // W38 (D176): the money fields that may read per rate or add columns; one in either mode is
  // not offered column by column.
  const multiable = multiFields(offered, rules)
  const byColumn = offered.filter((f) => !draft.multi[f.name] || !multiable.some((m) => m.name === f.name))
  const bound = new Set([
    ...Object.keys(draft.bindings).filter((f) => byColumn.some((o) => o.name === f)),
    ...multiable.filter((f) => multiDone(draft.multi[f.name])).map((f) => f.name),
  ])
  const short = multiable
    .filter((f) => (draft.multi[f.name]?.kind === 'sum' || draft.multi[f.name]?.kind === 'ledger') && !multiDone(draft.multi[f.name]))
    .map((f) => `${fieldLabel(f.name)} — ${draft.multi[f.name]?.kind === 'ledger' ? 'the ledger columns' : 'two or more columns to add'}`)
  const missing = [...missingBindings(fields, bound, mode, rules, fieldLabel).filter((m) => !short.some((s) => s.startsWith(`${m} —`))), ...short]
  const oneOfs = rules.oneOf.filter((rule) => rule.fields.every((n) => fields.some((f) => f.name === n && !f.required)))
  const oneOfItems = new Set(oneOfs.map((r) => r.item))
  const oneOfOf = (field: string) => oneOfs.find((r) => r.fields.includes(field)) ?? null
  const spec = specOf(draft, candidate, fields, rules)
  const key = JSON.stringify(spec)
  // A preview is of the map as it was when asked: any change since makes it another map's.
  const current = shown && shown.key === key ? shown.preview : null
  const label = documentTypeLabel(profile.name, types)
  const nameError = tried && !existing && !name.trim() ? 'Name the map — the layout it reads, the bank or the software the file came from.' : name.trim().length > MAX_MAP_NAME ? `Up to ${MAX_MAP_NAME} characters.` : null

  const change = (next: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...next }))
    setPreviewError('')
  }

  /** One field to one column: giving a column a field takes it from any column that had it. */
  const assign = (column: number, field: string) => {
    setDraft((d) => {
      const bindings = Object.fromEntries(Object.entries(d.bindings).filter(([f, at]) => at !== column && f !== field))
      if (field) bindings[field] = column
      return { ...d, bindings }
    })
    setPreviewError('')
  }

  const preview = async () => {
    if (!candidate || missing.length > 0 || previewing) return
    stop.current?.abort()
    const controller = new AbortController()
    stop.current = controller
    setPreviewing(true)
    setPreviewError('')
    try {
      const answer = await client.parsing.columnMaps.preview({ document_id: documentId, version_id: versionId ?? null, profile: profile.name, spec, wait: 20 })
      let job = answer.job
      let read = answer.preview
      // D122: a `202` is the job, followed until it ends and carries its reading.
      if (!jobEnded(job) && !read) {
        job = await followJob(client, job, { pollMs: jobPollMs, maxMs: PREVIEW_FOLLOW_MS, signal: controller.signal })
        read = job.preview ?? null
      }
      if (controller.signal.aborted) return
      if (read && job.status !== 'refused' && job.status !== 'failed') {
        setShown({ key, preview: read })
        onPreview?.(read)
      } else if (job.reason === 'mapping_needed') setPreviewError('This map does not fit the file. Check the sheet, the header row and the columns, then preview it again.')
      else if (!jobEnded(job)) setPreviewError('The preview is taking longer than usual. Try it again in a moment.')
      else setPreviewError(jobReasonText(job.reason) || 'ProDoc could not read the file under this map.')
    } catch (e) {
      if (!controller.signal.aborted) setPreviewError(mapErrorText(e, 'ProDoc could not preview it.'))
    } finally {
      if (!controller.signal.aborted) setPreviewing(false)
    }
  }

  const save = async () => {
    setTried(true)
    if (saving || missing.length > 0 || !current || !candidate) return
    if (!existing && (!name.trim() || name.trim().length > MAX_MAP_NAME)) return
    setSaving(true)
    setSaveError('')
    try {
      // The preview's fingerprint of this file's header row, so the map is chosen for files of this layout.
      const fingerprint = current.fingerprint ?? candidate.fingerprint ?? null
      const map = existing
        ? await client.parsing.columnMaps.addVersion(existing.id, { spec, fingerprint })
        : await client.parsing.columnMaps.create({ profile: profile.name, scope, name: name.trim(), spec, fingerprint })
      onSaved(map)
    } catch (e) {
      setSaveError(mapErrorText(e, 'The map was not saved.'))
    } finally {
      setSaving(false)
    }
  }

  if (fields.length === 0) {
    return <Alert tone="info">ProDoc reads {label.toLowerCase()} documents without a column map — there is nothing to map.</Alert>
  }

  return (
    <div className={cn('space-y-6', className)} data-column-mapper="">
      <p className="text-muted-foreground text-sm">
        {MAPPING_NEEDED_COPY} {scope === 'org' ? 'Saved for the organisation, it reads every file of this layout, whoever uploads it.' : 'Saved for this client, it reads their files of this layout.'}{' '}
        No model reads a mapped file, and a preview is not counted.
      </p>

      {/* The sheet, then the header row among the rows the engine offered. */}
      <section className="space-y-3" aria-labelledby="prodoc-map-header">
        <h3 id="prodoc-map-header" className="text-foreground text-sm font-semibold">
          The header row
        </h3>
        {sheets.length > 1 && (
          <Select
            id="prodoc-map-sheet"
            label="Sheet"
            value={draft.sheet ?? ''}
            onChange={(e) => {
              const sheet = e.target.value
              const first = candidatesOn(mapping, sheet)[0] ?? null
              change({ sheet, row: first?.row ?? null, ...(first && sheet === mapping.suggestion?.sheet ? bindingsOf(mapping.suggestion, first.cells, fields) : { bindings: {}, multi: {} }) })
            }}
          >
            {sheets.map((s) => (
              <option key={s.name} value={s.name}>
                {sheetName(s.name)}
                {s.rows > 0 ? ` · ${formatNumber(s.rows)} rows` : ''}
              </option>
            ))}
          </Select>
        )}
        {onSheet.length === 0 ? (
          <Alert tone="warning">ProDoc found no row on this sheet that looks like a header. Pick another sheet, or ask the file’s sender for one with a heading row.</Alert>
        ) : (
          <RadioGroup
            legend="Which row holds the column headings"
            options={onSheet.map((c) => ({ value: String(c.row), label: `Row ${c.row}`, description: cellsLine(c.cells) }))}
            value={draft.row === null ? null : String(draft.row)}
            onChange={(value) => {
              const row = Number(value)
              const next = onSheet.find((c) => c.row === row)
              // The columns stay where they were bound — a heading row changes their names, not their places.
              change({ row, bindings: next ? Object.fromEntries(Object.entries(draft.bindings).filter(([, at]) => at < next.cells.length)) : draft.bindings })
            }}
          />
        )}
      </section>

      {/* Each column given a field; the required ones marked. */}
      {candidate && (
        <section className="space-y-3" aria-labelledby="prodoc-map-columns">
          <h3 id="prodoc-map-columns" className="text-foreground text-sm font-semibold">
            The columns
          </h3>
          <div className="flex flex-wrap gap-1.5" aria-label="Fields to map" data-map-fields="">
            {offered.map((f) => {
              const done = bound.has(f.name)
              // W33: the date and the party are one of two — either names a ledger's rows (W38: a
              // profile's `oneOf` rules).
              const pair = oneOfOf(f.name)
              const needed = f.required || (pair !== null && !pair.fields.some((n) => bound.has(n)))
              const others = pair ? pair.fields.filter((n) => n !== f.name).map((n) => fieldLabel(n).toLowerCase()) : []
              return (
                <span key={f.name} data-field={f.name} data-bound={done ? 'yes' : 'no'} data-one-of={pair ? pair.fields.join('-or-') : undefined}>
                  <Badge tone={done ? 'success' : needed ? 'warning' : 'neutral'} size="sm">
                    {fieldLabel(f.name)}
                    {f.required ? ' · required' : pair ? ` · or ${others.join(' or ')}` : ''}
                  </Badge>
                </span>
              )
            })}
          </div>
          <ul className="divide-border border-border divide-y rounded-lg border" aria-label="Columns">
            {candidate.cells.map((cell, i) => {
              const letter = columnLetter(i)
              const field = Object.entries(draft.bindings).find(([f, at]) => at === i && byColumn.some((o) => o.name === f))?.[0] ?? ''
              return (
                <li key={`${letter}-${cell}`} className="grid items-center gap-2 px-3 py-2 sm:grid-cols-2" data-column={letter}>
                  <p className="text-foreground min-w-0 text-sm">
                    <span className="text-muted-foreground mr-2 font-mono text-xs">{letter}</span>
                    {cell.trim() ? cell : <span className="text-muted-foreground italic">No heading</span>}
                  </p>
                  <Select id={`prodoc-map-column-${letter}`} size="sm" aria-label={`Field for column ${letter}${cell.trim() ? `, ${cell.trim()}` : ''}`} value={field} onChange={(e) => assign(i, e.target.value)}>
                    <option value="">Not used</option>
                    {byColumn.map((f) => {
                      const pair = oneOfOf(f.name)
                      return (
                        <option key={f.name} value={f.name}>
                          {fieldLabel(f.name)}
                          {f.required ? ' (required)' : pair ? ` (${pair.fields.map((n) => fieldLabel(n).toLowerCase()).join(' or ')})` : ''}
                        </option>
                      )
                    })}
                  </Select>
                </li>
              )
            })}
          </ul>
          {multiable.length > 0 && (
            <MultiBindings
              fields={multiable}
              labelOf={fieldLabel}
              rules={rules}
              cells={candidate.cells}
              multi={draft.multi}
              onChange={(field, next) =>
                setDraft((d) => {
                  const multi = { ...d.multi }
                  if (next) multi[field] = next
                  else delete multi[field]
                  // A field read per rate or added holds no single column.
                  const bindings = next ? Object.fromEntries(Object.entries(d.bindings).filter(([f]) => f !== field)) : d.bindings
                  return { ...d, multi, bindings }
                })
              }
            />
          )}
          {missing.some((m) => !oneOfItems.has(m)) && (
            <p className="text-muted-foreground text-sm" role="status" data-missing="">
              Still to map: {missing.filter((m) => !oneOfItems.has(m)).join(', ')}.
            </p>
          )}
          {oneOfs
            .filter((rule) => missing.includes(rule.item))
            .map((rule) => (
              <p key={rule.item} className="text-muted-foreground text-sm" role="status" data-missing-one-of={rule.fields.join('-or-')}>
                {rule.text}
              </p>
            ))}
        </section>
      )}

      {/* The rules: dates, money, rows to skip, where the table ends. */}
      <section className="space-y-4" aria-labelledby="prodoc-map-rules">
        <h3 id="prodoc-map-rules" className="text-foreground text-sm font-semibold">
          How it is read
        </h3>
        <Select
          id="prodoc-map-date-format"
          label="Dates are printed"
          value={draft.dateFormat ?? ''}
          onChange={(e) => change({ dateFormat: (MAP_DATE_FORMATS as readonly string[]).includes(e.target.value) ? (e.target.value as MapDateFormat) : null })}
          hint="Read as printed, the day first, unless the file says otherwise."
        >
          <option value="">As printed, the day first</option>
          {MAP_DATE_FORMATS.map((f) => (
            <option key={f} value={f}>
              {f} — {DATE_FORMAT_TEXT[f]}
            </option>
          ))}
        </Select>
        {hasMoneyModes(fields, rules) && (
          <RadioGroup<MoneyMode>
            legend="How money is signed"
            options={[
              { value: 'debit_credit', label: 'A debit column and a credit column' },
              { value: 'signed', label: 'One amount column', description: 'Positive is a debit, negative a credit.' },
            ]}
            value={draft.mode}
            onChange={(mode) => change({ mode })}
          />
        )}
        <fieldset className="space-y-2">
          <legend className="text-muted-foreground mb-1 text-sm">Rows to skip</legend>
          <Checkbox label="Blank rows" checked={draft.skipBlank} onChange={(e) => change({ skipBlank: e.target.checked })} />
          <Checkbox label="The heading row printed again (on each page)" checked={draft.skipRepeat} onChange={(e) => change({ skipRepeat: e.target.checked })} />
          {(draft.skipTexts.length > 0 ? draft.skipTexts : ['']).map((text, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  id={`prodoc-map-skip-${i}`}
                  label={i === 0 ? 'A row holding this text — a total line' : 'And a row holding this text'}
                  value={text}
                  autoComplete="off"
                  onChange={(e) => {
                    const texts = draft.skipTexts.length > 0 ? [...draft.skipTexts] : ['']
                    texts[i] = e.target.value
                    change({ skipTexts: texts })
                  }}
                  hint={i === 0 ? 'Such as “Total” or “Sub total”. Leave it empty for none.' : undefined}
                />
              </div>
              {draft.skipTexts.length > 1 && (
                <Button type="button" variant="ghost" size="sm" aria-label={`Do not skip rows holding “${text}”`} onClick={() => change({ skipTexts: draft.skipTexts.filter((_, j) => j !== i) })}>
                  <X size={15} aria-hidden />
                </Button>
              )}
            </div>
          ))}
          {draft.skipTexts.some((t) => t.trim()) && (
            <Button type="button" variant="link" onClick={() => change({ skipTexts: [...draft.skipTexts, ''] })}>
              <Plus size={14} aria-hidden />
              Another text to skip
            </Button>
          )}
        </fieldset>
        <Input
          id="prodoc-map-stop-at"
          label="The table ends at a row holding"
          value={draft.stopAt}
          autoComplete="off"
          onChange={(e) => change({ stopAt: e.target.value })}
          hint="Such as “Closing balance” or “Grand total”. Leave it empty to read to the last row."
        />
      </section>

      {/* The engine's own reading of it under this map — the first rows, the summary and the checks. */}
      <section className="space-y-3" aria-labelledby="prodoc-map-preview">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="prodoc-map-preview" className="text-foreground text-sm font-semibold">
            Preview
          </h3>
          <Button type="button" variant="outline" size="sm" onClick={() => void preview()} loading={previewing} loadingText="Reading…" disabled={!candidate || missing.length > 0}>
            <Eye size={15} aria-hidden />
            {current ? 'Preview again' : 'Preview'}
          </Button>
        </div>
        {previewError && <Alert tone="error">{previewError}</Alert>}
        {current ? (
          <PreviewView preview={current} fields={fields} spec={spec} labelOf={fieldLabel} />
        ) : (
          <p className="text-muted-foreground text-sm">
            {shown ? 'The map changed since the preview. Preview it again to see how it reads now.' : 'See the first rows as ProDoc reads them under this map, with its totals and checks, before it is saved.'}
          </p>
        )}
      </section>

      {/* Save: a new map, or a new version of the one being changed. */}
      <section className="space-y-3" aria-labelledby="prodoc-map-save">
        <h3 id="prodoc-map-save" className="text-foreground text-sm font-semibold">
          {existing ? `A new version of “${existing.name}”` : 'Save the map'}
        </h3>
        {!existing && (
          <Input
            id="prodoc-map-name"
            label="Name"
            value={name}
            maxLength={MAX_MAP_NAME + 1}
            autoComplete="off"
            onChange={(e) => setName(e.target.value)}
            hint="The layout it reads — the bank, the branch or the accounting software the file came from."
            error={nameError}
          />
        )}
        {existing && <p className="text-muted-foreground text-sm">Its earlier versions stay, and what was read under them is unchanged.</p>}
        {saveError && <Alert tone="error">{saveError}</Alert>}
        {!current && missing.length === 0 && candidate && <p className="text-muted-foreground text-xs">Preview it first — the map is saved as the preview read it.</p>}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="button" onClick={() => void save()} loading={saving} loadingText="Saving…" disabled={missing.length > 0 || !current || !candidate}>
            {existing ? 'Save as a new version' : 'Save the map'}
          </Button>
        </div>
      </section>
    </div>
  )
}

/** The words for a per-rate head: `taxable` → "Taxable", `igst` → "IGST". */
const headLabel = (head: string) => fieldLabel(head)

/**
 * W38 (D176, D179): the money fields that may be read from more than one column — each
 * from one column (the list above), **one column per rate** (`rate:<head>`, the columns that
 * print a rate before the head's name), or **several columns added** (a taxable value spread
 * over expense-ledger columns).
 */
function MultiBindings({
  fields,
  labelOf = fieldLabel,
  rules,
  cells,
  multi,
  onChange,
}: {
  fields: readonly MapField[]
  labelOf?: (field: string) => string
  rules: MapperRules
  cells: readonly string[]
  multi: Record<string, MultiBinding>
  onChange: (field: string, next: MultiBinding | null) => void
}) {
  const fieldLabel = labelOf
  const ledgers = new Set(rules.ledgerOf ?? [])
  return (
    <fieldset className="border-border space-y-3 rounded-lg border p-3" data-multi-bindings="">
      <legend className="text-foreground px-1 text-sm font-medium">Figures spread over several columns</legend>
      <p className="text-muted-foreground text-xs">
        A register may print a column per GST rate — 5% Taxable, 18% Taxable — or spread the taxable value over one column per expense ledger. Each line then reads them all; the rate is taken from each column’s heading.
        {ledgers.size > 0 && ' A line’s item may be the ledger it is posted to: the one ledger column holding its figure.'}
      </p>
      {fields.map((f) => {
        const current = multi[f.name]
        const kind = current?.kind ?? 'one'
        const id = `prodoc-map-multi-${f.name}`
        return (
          <div key={f.name} className="space-y-2" data-multi-field={f.name} data-multi-kind={kind}>
            <Select
              id={id}
              size="sm"
              label={`${fieldLabel(f.name)} is read from`}
              value={kind}
              onChange={(e) => {
                const value = e.target.value
                if (value === 'rate') onChange(f.name, { kind: 'rate', head: defaultHead(f.name, rules.rateHeads) })
                else if (value === 'sum') onChange(f.name, { kind: 'sum', columns: [] })
                else if (value === 'ledger') onChange(f.name, { kind: 'ledger', columns: [] })
                else onChange(f.name, null)
              }}
            >
              <option value="one">One column (chosen above)</option>
              {f.kind === 'money' && rules.rateHeads.length > 0 && <option value="rate">One column per rate</option>}
              {f.kind === 'money' && rules.summed && <option value="sum">Several columns, added</option>}
              {f.kind !== 'money' && ledgers.has(f.name) && <option value="ledger">The ledger column a line is posted to</option>}
            </Select>
            {current?.kind === 'ledger' && (
              <ul className="grid gap-1 sm:grid-cols-2" aria-label={`Ledger columns naming ${fieldLabel(f.name)}`} data-ledger-columns="">
                {cells.map((cell, i) => {
                  const letter = columnLetter(i)
                  const on = current.columns.includes(i)
                  return (
                    <li key={`${letter}-${cell}`}>
                      <Checkbox
                        label={`${letter} · ${cell.trim() || 'No heading'}`}
                        checked={on}
                        disabled={!on && current.columns.length >= MAX_SUMMED_COLUMNS}
                        onChange={(e) => onChange(f.name, { kind: 'ledger', columns: e.target.checked ? [...current.columns, i] : current.columns.filter((c) => c !== i) })}
                      />
                    </li>
                  )
                })}
              </ul>
            )}
            {current?.kind === 'rate' && (
              <RateHead heads={rules.rateHeads} head={current.head} cells={cells} id={`${id}-head`} onChange={(head) => onChange(f.name, { kind: 'rate', head })} />
            )}
            {current?.kind === 'sum' && (
              <ul className="grid gap-1 sm:grid-cols-2" aria-label={`Columns added for ${fieldLabel(f.name)}`} data-sum-columns="">
                {cells.map((cell, i) => {
                  const letter = columnLetter(i)
                  const on = current.columns.includes(i)
                  return (
                    <li key={`${letter}-${cell}`}>
                      <Checkbox
                        label={`${letter} · ${cell.trim() || 'No heading'}`}
                        checked={on}
                        disabled={!on && current.columns.length >= MAX_SUMMED_COLUMNS}
                        onChange={(e) => onChange(f.name, { kind: 'sum', columns: e.target.checked ? [...current.columns, i] : current.columns.filter((c) => c !== i) })}
                      />
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )
      })}
    </fieldset>
  )
}

/** The head a per-rate binding reads, and the columns of this header that look like it — shown, never decided here. */
function RateHead({ heads, head, cells, id, onChange }: { heads: readonly string[]; head: string; cells: readonly string[]; id: string; onChange: (head: string) => void }) {
  const found = rateColumns(cells, head)
  const options = heads.includes(head) ? heads : [...heads, head]
  return (
    <div className="space-y-1">
      <Select id={id} size="sm" label="Columns headed with a rate and" value={head} onChange={(e) => onChange(e.target.value)}>
        {options.map((h) => (
          <option key={h} value={h}>
            {headLabel(h)}
          </option>
        ))}
      </Select>
      {found.length > 0 ? (
        <p className="text-muted-foreground text-xs" data-rate-columns="">
          On this header row: {found.map((i) => `${columnLetter(i)} ${cells[i]!.trim()}`).join(' · ')}.
        </p>
      ) : (
        <p className="text-muted-foreground text-xs" data-rate-columns="none">
          No column on this header row prints a rate before “{headLabel(head)}”. The preview shows what is read.
        </p>
      )}
    </div>
  )
}

/** A header candidate's cells on one line — the first twelve with text, as printed. */
function cellsLine(cells: readonly string[]): string {
  const printed = cells.map((c) => c.trim()).filter(Boolean)
  if (printed.length === 0) return 'No headings'
  return printed.length > 12 ? `${printed.slice(0, 12).join(' · ')} · …` : printed.join(' · ')
}

type PreviewRow = { __n: number; values: Record<string, unknown> }

/** The preview's rows, its summary and its checks — money in rupees from integer paise. */
function PreviewView({ preview, fields, spec, labelOf = fieldLabel }: { preview: MapPreview; fields: readonly MapField[]; spec: MapSpec; labelOf?: (field: string) => string }) {
  const fieldLabel = labelOf
  const kinds = new Map(fields.map((f) => [f.name, f.kind]))
  // The fields this map binds, in the profile's order — what the person asked to be read.
  const keys = fields.map((f) => f.name).filter((name) => name in spec.columns)
  const columns: ColumnDef<PreviewRow>[] = [
    { key: '__n', label: '#', width: 48, render: (r) => r.__n },
    ...keys.map(
      (k): ColumnDef<PreviewRow> => ({
        key: k,
        label: fieldLabel(k),
        width: kinds.get(k) === 'money' ? 120 : 140,
        align: kinds.get(k) === 'money' || kinds.get(k) === 'integer' ? 'right' : 'left',
        render: (r) => {
          const value = r.values[k]
          if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">—</span>
          return <span className={cn(kinds.get(k) === 'money' && 'tabular-nums')}>{fieldText(value, kinds.get(k) === 'money' && Number.isSafeInteger(value) ? 'money' : 'text')}</span>
        },
      }),
    ),
  ]
  const rows = preview.rows.map((values, i) => ({ __n: i + 1, values }))
  const lists = preview.summary?.lists ?? {}
  const count = lists.entries ?? Object.values(lists)[0] ?? rows.length
  const totals = Object.entries(preview.summary?.totals ?? {})
  return (
    <div className="space-y-4" data-preview="">
      <div className="flex flex-wrap items-center gap-2">
        {/* Nothing broke, but nothing gated either (a ledger with no balances printed): say so, never "Does not add up". */}
        {!preview.reconciled && !preview.checks.some((c) => c.result === 'broken') ? (
          <Badge tone="neutral" size="sm">
            Nothing to check it against
          </Badge>
        ) : (
          <ReconciledBadge reconciled={preview.reconciled} size="sm" />
        )}
        {preview.truncated && <span className="text-muted-foreground text-xs">The first {formatNumber(rows.length)} rows are shown; the file has more.</span>}
      </div>
      <DescriptionList>
        <Detail label="Entries" value={formatNumber(count)} />
        {totals.map(([name, paise]) => (
          <Detail key={name} label={`${fieldLabel(name)} total`} value={fieldText(paise, 'money')} />
        ))}
      </DescriptionList>
      <DataTable columns={columns} rows={rows} rowKey={(r) => String(r.__n)} maxHeight="40vh" empty={<p className="text-muted-foreground p-4 text-sm">No rows were read under this map.</p>} />
      <ChecksCard checks={preview.checks} rechecks={null} ordered />
    </div>
  )
}
